import React from 'react';
import { Navigate, Outlet, useOutletContext } from 'react-router-dom';
import { useMyReferralProgramAccess, type PartnerReferralsGateContext } from '../../../hooks/useMyReferralProgramAccess';
import { PartnerReferralsSkeleton } from '../PartnerReferralsPage';

/**
 * Route gate for /partner-referrals once the desk exists (replaces
 * RequirePartnerNetworkAccess on that route only). Admits:
 *
 *   - the referrer side, exactly as before (canAccessPartnerNetwork: the
 *     flag/add-on precedence plus PARTNER_NETWORK_TENANT_ROLES);
 *   - a desk role (super_admin, administrator, marketing) whose company
 *     operates at least one active referral program (partnerDeskGetContext).
 *
 * It waits for the add-on answer and, for a desk-only viewer, the program
 * list, instead of redirecting while they load. Same predicate as the side
 * nav (canSeePartnerReferrals); the loyalty callables re-check everything.
 */
const RequirePartnerReferralsArea: React.FC = () => {
  const outletContext = useOutletContext<PartnerReferralsGateContext>();
  const my = useMyReferralProgramAccess(outletContext);
  if (my.addOnLoading) return <PartnerReferralsSkeleton />;
  if (my.referrerSide) return <Outlet context={outletContext} />;
  if (!my.deskEligible) return <Navigate to="/" replace />;
  if (my.hasProgram) return <Outlet context={outletContext} />;
  if (my.desk.status === 'idle' || my.desk.status === 'loading') return <PartnerReferralsSkeleton />;
  return <Navigate to="/" replace />;
};

export default RequirePartnerReferralsArea;
